import React, { useContext, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Icon,
  InputProps,
  Popover,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react-v3';

import { useGetUserInformation, logger } from 'appSelectorContext';
import { uniqueId } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { featureFlags } from '../../argoConfig';
import { RECEIVE_TYPES_UI } from '../../constants';
import useLocalStorage from '../../hooks/useLocalStorage';
import { Context, Types } from '../../store';
import {
  ASNReferenceForPO,
  Model,
  MPNToIPNMapping,
  MPNToIPNPair,
  ReceiveType,
  ReferenceQueryResponseItem,
  URIParams,
} from '../../store/types';
import ScanInput from '../ScanInput';
import { MultipleReferences } from '../StartReceiving/MultipleReferences';
import { DEFAULT_TABLE_TOTAL_ROWS } from '../Utils/Utils';
import ColumnDefinitions, { contentDisplay, contentDisplayPrefernece, visibleColumns } from './ColumnDefinitions';
import { areMultipleManufacturersPresent } from './DashrollUtilities';
import { ClientError, ErrorMessages } from './ErrorMessages';
import { MPNAutoSuggestion } from './MPNAutoAuggest';
import { MPNToIPNMappings } from './MPNToIPNMappings';
import { ManufacturerSelectionModal } from './ManufacturerSelectionModal';
import { ModelNotInASNConfirmation } from './ModelNotInASNConfirmation';
import { MultipleIPNModels } from './MultipleIPNModels';
import { MODEL_ERROR_CODES } from './constants';
import useASNPOSearch from './useASNPOSearch';
import useMPNsToReceive from './useMPNsToReceive';
import { useMpnSearch } from './useMpnSearch';

import './models.scss';

export const DEFAULT_PREFERENCES = {
  pageSize: 10,
  wrapLines: true,
  visibleContent: visibleColumns,
  contentDisplay: contentDisplay,
};

interface ModelsToReceiveParams {
  receiveReason?: string | null;
  selectedSite?: string | null;
  notificationItems?: any;
  setNotificationItems?: (items: any) => void;
}

export const MPNsToReceive = ({
  receiveReason,
  selectedSite,
  notificationItems,
  setNotificationItems,
}: ModelsToReceiveParams) => {
  const { state, dispatch } = useContext(Context);
  const { referenceId, receiveType } = useParams<URIParams>();
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const isRMA = receiveType === 'no_reference';
  const disableSearch = isAdjustment || isRMA;

  const history = useHistory();
  const [key, setKey] = useState(uniqueId());
  const [mpnInput, setMpnINput] = useState('');
  const [errorText, setErrorText] = useState('');
  const [hideSuggest, setHideSuggest] = useState(false);
  const [mpnSearch, setMpnSearch] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [mpnMappingSelected, setMpnMappingSelected] = useState<MPNToIPNPair | null>(null);
  const cancelPopover = () => setKey(uniqueId());
  const context = useGetUserInformation();

  const [preferences, setPreferences] = useLocalStorage('model-preferences-table-v2', DEFAULT_PREFERENCES);
  const [selected, setSelected] = useState<Model[]>([]);

  const {
    searchModels,
    loading,
    asns,
    setAsns,
    mpnToIPNMapping,
    setMPNToIPNMapping,
    selectedModel,
    error,
    setError,
    warning,
    setWarning,
    request,
  } = useMPNsToReceive({ siteName: selectedSite || '', orderType: receiveType, receiveReason, mpnInput });

  const { suggestions, closePopover, cancelSubscription } = useMpnSearch({
    expectedMPNPrefix: mpnInput,
    referenceId,
    disableSearch,
  });

  const {
    asnPos,
    searchAsnPo,
    setAsnPos,
    error: asnPOerror,
    variables: asnPoSearchRequest,
    asnPOIndx,
    canFetchMore,
    refetchAsns,
    asnLoading,
  } = useASNPOSearch({
    expectedMPN: mpnInput,
    referenceId,
    onCompleted: (asnReferencesList: ASNReferenceForPO[]) => {
      if (!asnReferencesList || asnReferencesList?.length === 0 || !!asnPOerror) {
        setMpnSearch(true);
        const request = {
          receiveReferenceIdList: [referenceId],
          mpn: asnPoSearchRequest?.mpn,
        };
        searchModels({
          variables: request,
        });
        return;
      }
    },
  });

  // Record whether there are multiple manufacturers present
  const multipleManufacturersPresent: boolean = areMultipleManufacturersPresent(mpnToIPNMapping?.associatedIpns);

  // Show MPN Search only if expectedMPN is not present and search is not disabled
  const showMPNSearch = !disableSearch && !selectedModel?.expectedMPN && !loading;

  const totalAsnPos = asnPos.slice(
    (asnPOIndx - 1) * DEFAULT_TABLE_TOTAL_ROWS,
    (asnPOIndx - 1) * DEFAULT_TABLE_TOTAL_ROWS + DEFAULT_TABLE_TOTAL_ROWS,
  );

  const models = useMemo(() => {
    if (isAdjustment) {
      return state.models.filter(
        (model: Model) =>
          model.orderType === ReceiveType.INVENTORY_ADJUSTMENT &&
          model.siteName === selectedSite &&
          model.referenceId === referenceId,
      );
    }
    return state.models.filter((model: Model) => {
      if (model.siteName && selectedSite) {
        return model.referenceId === referenceId && model.siteName === selectedSite;
      }
      return model.referenceId === referenceId;
    });
  }, [isAdjustment, state.models, selectedSite, referenceId]);

  const columnDefinitions = useMemo(() => {
    if (isRMA) {
      return ColumnDefinitions.DefinitionsForRMA;
    }

    if (isAdjustment) {
      return ColumnDefinitions.DefinitionsForIA;
    }
    return ColumnDefinitions.Definitions;
  }, [isAdjustment, isRMA]);

  const multipleIPNModels = asns?.length > 1;
  const hasWarnings = warning;
  const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
  const reference = references.find(
    (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === referenceId,
  );
  const isPO =
    reference?.receiveReference.externalReferenceType &&
    [ReceiveType.AWS_PO].includes(reference?.receiveReference.externalReferenceType);

  const isASNPO =
    reference?.receiveReference.externalReferenceType &&
    [ReceiveType.ASN_AWS_PO].includes(reference?.receiveReference.externalReferenceType);

  const onInputChange = (val: string) => {
    setError(null);
    setErrorText('');
    if (hideSuggest || val.length > 3) {
      setHideSuggest(false);
    }
    setMpnINput(val);
  };

  const handleClosePopOver = (hide = false) => {
    closePopover();
    setActiveIndex(-1);
    if (suggestions?.values?.length <= 0 || hide) {
      setHideSuggest(true);
    }
  };

  const onKeyDown = (detail: InputProps.KeyDetail) => {
    switch (detail.key) {
      case 'ArrowDown':
        activeIndex < 0 && setActiveIndex(activeIndex + 1);
        return;
      case 'ArrowUp':
        activeIndex > -1 && setActiveIndex(activeIndex - 1);
        return;
      case 'Escape':
        handleClosePopOver(true);
        return;
      default:
    }
  };

  const showMultipleModels = multipleIPNModels && !mpnToIPNMapping;

  const resetErrorsAndWarnings = () => {
    setError(null);
    setWarning(false);
  };

  const searchMPN = (modelId: string, refId = referenceId) => {
    const request = {
      receiveReferenceIdList: [refId],
      mpn: modelId,
      noRefShipmentId: isRMA ? reference?.receiveReference.externalReferenceValue : undefined,
    };
    searchModels({
      variables: request,
    });
    setMpnINput('');
    cancelSubscription();
  };

  const handleScanModel = (modelId: string) => {
    if (activeIndex === 0) {
      const selectedMpn = suggestions?.values?.[0]?.validMPN;
      setMpnINput(selectedMpn);
      handleClosePopOver();
      return;
    }
    resetErrorsAndWarnings();

    if (isPO && featureFlags?.showReleaseOneExperience && false) {
      setMpnSearch(false);
      searchAsnPo({
        variables: {
          mpn: modelId,
          externalReferenceValue: reference?.receiveReference?.externalReferenceValue,
        },
      });
      return;
    }

    searchMPN(modelId);
  };

  const handleModelConfirm = (model: Model) => {
    const mpnIpnId = uuidv4();

    // Get mappedIPN and description from selected model
    const mappedIPN = selectedModel?.mappedIPN ?? model?.mappedIPN ?? selectedModel?.expectedIPN ?? model?.expectedIPN;
    const mappedDescription = selectedModel?.description ?? model?.description;

    const mappedModel = mpnMappingSelected
      ? {
          // We had to remove the following line to get the OrderNumber to appear. In case this is wrong,
          // I am leaving it in
          // ...selectedModel,

          ...model,
          expectedIPN: mpnMappingSelected?.ipn,
          description: mpnMappingSelected?.description,
          manufacturer: mpnMappingSelected?.manufacturer,
          expectedMaterialDefinitionId: mpnMappingSelected?.materialDefintionId,
        }
      : // We had to remove the following line to get the OrderNumber to appear. In case this is wrong,
        // I am leaving it in
        // : selectedModel;
        {
          ...model,
          expectedIPN: mappedIPN,
          description: mappedDescription,
        };
    resetErrorsAndWarnings();
    dispatch({
      type: Types.scanModel,
      payload: {
        ...mappedModel,
        modelNumber: selectedModel?.modelNumber,
        mpnIpnId: mpnIpnId,
        referenceId,
        siteName: selectedSite,
        orderType: receiveType,
        receiveReason: receiveReason,
        buildCategory: model.buildCategory,
        buildId: model.buildId,
        mappedIPN: mappedModel?.expectedIPN,
        manufacturer: mappedModel?.manufacturer,
        referenceLineItemId: model?.referenceLineItemId,
        orderedQuantity: model?.orderedQuantity,
        maximumExpectedQuantity: model?.maximumExpectedQuantity,
        currentReceivedQuantity: model?.currentReceivedQuantity,
        orderLineItemNumber: model?.orderLineItemNumber,
        tagList: model?.tagList,
        referenceLineItemStatus: model?.referenceLineItemStatus,
      },
    });
    setAsns([]);
    mpnIpnId && history.push(`/receive/${receiveType}/${referenceId}/model/${mpnIpnId}`);
  };

  const onHandleDismissWarning = () => {
    resetErrorsAndWarnings();
    setMPNToIPNMapping(undefined);
  };

  const onConfirmMultipleReferences = (reference: any) => {
    const { receiveReference } = reference;
    dispatch({
      type: Types.scanReference,
      payload: {
        errors: [],
        loading: false,
        data: {
          receiveReferenceQueryResponseList: [
            {
              ...reference,
              scanStartTime: Date.now(),
              scannableIdFoundAs: RECEIVE_TYPES_UI.SHIPMENT_ID,
            },
          ],
        },
      },
    });

    setAsnPos([]);
    history.push(`/receive/shipment/${receiveReference?.receiveReferenceId}`);
    const notifications = notificationItems.concat({
      type: 'success',
      content: `Switched to ASN Receive as choosen`,
      dismissible: true,
      dismissLabel: 'Dismiss message',
      onDismiss: () => setNotificationItems && setNotificationItems([]),
      id: 'po_asn_changed_message',
    });

    setNotificationItems && setNotificationItems(notifications);
    searchMPN(mpnInput, receiveReference?.receiveReferenceId);
  };

  const onCancelMultipleReferences = () => {
    setAsnPos([]);
    searchMPN(mpnInput);
  };

  const handleDismissAsnISelection = () => {
    setAsns([]);
    setMPNToIPNMapping(undefined);
    resetErrorsAndWarnings();
  };

  /**
   * Updates MPN/IPN mappings, line item selection, and handles MPN selection based off the manufacturer set by the user.
   * @param manufacturer Manufacturer selected by the user
   */
  const handleManufacturerSelected = (manufacturer: string): void => {
    // Get list of entries filtered by the specified manufacturer
    const filteredIpnEntriesByManufacturer: MPNToIPNPair[] = mpnToIPNMapping.associatedIpns.filter(
      (ipnEntry): boolean => ipnEntry.manufacturer === manufacturer,
    );
    const resultingIPNs: Set<string> = new Set(
      filteredIpnEntriesByManufacturer.map((mpnToIpnPair: MPNToIPNPair) => mpnToIpnPair.ipn),
    );

    // Update MPN/IPN mappings to exclude entries from non-selected manufacturer
    const updatedMpnToIpnMappings: MPNToIPNMapping = {
      mpn: mpnToIPNMapping.mpn,
      associatedIpns: filteredIpnEntriesByManufacturer,
    };
    setMPNToIPNMapping(updatedMpnToIpnMappings);

    // Filter matching line items as a result of getting a more restricted set of MPN/IPN mappings
    const filteredLineItemsByExpectedIPN: Model[] = asns?.filter((lineItem) =>
      resultingIPNs.has(lineItem?.expectedIPN),
    );
    // Only filter if resulting line item list would have entries. Otherwise, leave the selection be for later selection by user.
    if (filteredLineItemsByExpectedIPN.length > 0) {
      setAsns(filteredLineItemsByExpectedIPN);
    }
    // If there is a single entry to use, select it.

    const singleEntry: boolean = updatedMpnToIpnMappings?.associatedIpns?.length === 1;
    if (singleEntry) {
      handleMpnMappingSelected(updatedMpnToIpnMappings.associatedIpns[0]);
    }
  };

  const handleMpnMappingSelected = (mpnIpnPair: MPNToIPNPair) => {
    setMpnMappingSelected(mpnIpnPair);
    const mpnIpnId = selectedModel?.mpnIpnId || uuidv4();

    const asnMappings: Model[] =
      asns?.filter((m) => {
        return (!m?.mappedIPN) || m?.mappedIPN === mpnIpnPair?.ipn;
      }) ?? [];
    const asnMapping = asnMappings.length > 1 ? asnMappings : asnMappings?.[0];

    if (asnMappings.length > 1) {
      setAsns(asnMappings.map((a) => ({ ...a, description: mpnIpnPair.description })));
      setMPNToIPNMapping(undefined);
      return;
    }

    const oneAsn = asnMappings?.length < 2 && asns?.length < 2;
    if (!oneAsn && !asnMapping) {
      setMPNToIPNMapping(undefined);
      return;
    }
    const selectedAsn: Model = asns?.[0];
    const asnModelPayload = asnMapping || {};

    dispatch({
      type: Types.scanModel,
      payload: {
        ...asnModelPayload,
        mpnIpnId,
        modelNumber: mpnToIPNMapping?.mpn,
        mappedIPN: mpnIpnPair?.ipn,
        manufacturer: mpnIpnPair?.manufacturer,
        description: mpnIpnPair?.description,
        expectedMPN: mpnToIPNMapping?.mpn,
        expectedMaterialDefinitionId: mpnIpnPair?.materialDefintionId,
        referenceId,
        siteName: selectedSite,
        orderType: receiveType,
        receiveReason: receiveReason,
        buildCategory: asnMapping ? asnMapping?.buildCategory : selectedAsn?.buildCategory,
        buildId: asnMapping ? asnMapping?.buildId : selectedAsn?.buildId,
        tagList: asnMapping ? asnMapping?.tagList : selectedAsn?.tagList,
        referenceLineItemStatus: asnMapping
          ? asnMapping?.referenceLineItemStatus
          : selectedAsn?.referenceLineItemStatus,
        referenceLineItemId: asnMapping ? asnMapping?.referenceLineItemId : selectedAsn?.referenceLineItemId,
        maximumExpectedQuantity: asnMapping
          ? asnMapping?.maximumExpectedQuantity
          : selectedAsn?.maximumExpectedQuantity,
        currentReceivedQuantity: asnMapping
          ? asnMapping?.currentReceivedQuantity
          : selectedAsn?.currentReceivedQuantity,
        orderedQuantity: asnMapping ? asnMapping?.orderedQuantity : selectedAsn?.orderedQuantity,
      },
    });
    !!asnMapping && setAsns([]);
    const multiASNIpns = !!multipleIPNModels && !asnMapping;
    mpnIpnId && !multiASNIpns && history.push(`/receive/${receiveType}/${referenceId}/model/${mpnIpnId}`);
    setMPNToIPNMapping(undefined);
  };

  const handleDeleteModel = () => {
    const mpnIpnIds: string[] = selected.map((s) => s?.mpnIpnId);
    logger.info(
      `deleting models: ${JSON.stringify({
        mpnIpnIds: mpnIpnIds,
        withUser: context.user,
      })} `,
    );
    dispatch({
      type: Types.deleteModel,
      payload: {
        mpnIpnIds: mpnIpnIds,
      },
    });
    cancelPopover();
    setSelected([]);
  };

  const handleEditModel = () => {
    const model: Model = selected?.[0];
    history.push(`/receive/${receiveType}/${referenceId}/model/${model?.mpnIpnId}`);
  };

  const handleScanModelButtonClick = () => {
    if (mpnInput?.length === 0) {
      return;
    }

    const regexp = new RegExp('^[a-zA-Z0-9_.-]*$');
    if (!regexp.test(mpnInput?.toUpperCase())) {
      setErrorText(`Scan MPN should not contain space or certain special characters`);
      return;
    }

    logger.info(
      `adding model with button click: ${JSON.stringify({
        mpnInput,
        withUser: context.user,
      })} `,
    );
    handleScanModel(mpnInput);
  };

  const onHandleClickOption = (mpn: string) => {
    resetErrorsAndWarnings();
    const request = {
      receiveReferenceIdList: [referenceId],
      mpn,
    };

    if (isPO && featureFlags?.showReleaseOneExperience && false) {
      setMpnSearch(false);
      searchAsnPo({
        variables: {
          mpn: mpn,
          externalReferenceValue: reference?.receiveReference?.externalReferenceValue,
        },
      });
      return;
    }

    logger.info(
      `adding model with mpn auto auggest option click: ${JSON.stringify({
        request,
        withUser: context.user,
      })} `,
    );
    searchModels({
      variables: request,
    });
    setMpnINput('');
    cancelSubscription();
  };

  const onHandleConfirmWarningMPN = () => {
    if (selectedModel?.mpnIpnId) {
      dispatch({
        type: Types.scanModel,
        payload: selectedModel,
      });
      logger.info(
        `handle confirm warning mpn : ${JSON.stringify({
          selectedModel,
          withUser: context.user,
        })} `,
      );
      history.push(`/receive/${receiveType}/${referenceId}/model/${selectedModel?.mpnIpnId}`);
    }
    resetErrorsAndWarnings();
  };

  // This code was introduced to handle the use case where only one IPN is applied. If there start to be issues.
  // Move this to a useEffect or similar place outside of render code.
  const singleIpn = mpnToIPNMapping?.associatedIpns?.[0] && mpnToIPNMapping?.associatedIpns?.length === 1;
  if (singleIpn) {
    handleMpnMappingSelected(mpnToIPNMapping.associatedIpns[0]);
  }

  return (
    <Box>
      <Table
        ariaLabels={{
          allItemsSelectionLabel: ({ selectedItems }) =>
            `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`,
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter((i: Model) => i.mpnIpnId === item.mpnIpnId).length;
            return `${item.expectedIPN} is ${isItemSelected ? '' : 'not '}selected`;
          },
        }}
        columnDefinitions={columnDefinitions}
        items={models}
        loadingText="Loading models"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No Models</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No models to display.
            </Box>
          </Box>
        }
        loading={loading}
        footer={null}
        selectionType="multi"
        // visibleColumns={preferences.visibleContent}
        columnDisplay={preferences.contentDisplay}
        onSelectionChange={({ detail }) => {
          setSelected(detail?.selectedItems);
        }}
        trackBy="mpnIpnId"
        selectedItems={selected}
        onRowClick={({ detail }) => {
          const isRowSelected = selected.find((s) => s.mpnIpnId === detail?.item?.mpnIpnId);
          if (isRowSelected) {
            setSelected(selected.filter((s) => s.mpnIpnId !== isRowSelected.mpnIpnId));
          } else {
            setSelected(selected.concat(detail?.item));
          }
        }}
        stickyHeader
        resizableColumns
        stickyColumns={{ first: 1, last: 0 }}
        header={
          <Header
            description={
              <Box color="text-status-inactive" fontSize="body-s" variant="span">
                <ErrorMessages code="model_not_available_to_scan" />
              </Box>
            }
            actions={
              <div className="model-actions">
                <SpaceBetween direction="horizontal" size="xs">
                  {false && (
                    <Button
                      disabled={selected.length === 0}
                      variant="link"
                      ariaLabel="edit mpn"
                      onClick={handleEditModel}
                    >
                      Edit Model
                    </Button>
                  )}
                  {selected.length === 0 ? (
                    <Button disabled={selected.length === 0}>Delete Model</Button>
                  ) : (
                    <Popover
                      key={key}
                      dismissButton={false}
                      position="top"
                      size="large"
                      triggerType="custom"
                      content={
                        <div className="model-delete">
                          <p>Are you sure you want to delete these scanned MPN's ?</p>
                          <p>
                            This action will remove both drafts and received MPN's and you will lose data from any
                            pending drafts.
                          </p>
                          <div className="confirm-actions">
                            <SpaceBetween direction="horizontal" size="xs">
                              <Button variant="link" onClick={cancelPopover}>
                                Cancel
                              </Button>
                              <Button
                                disabled={selected.length === 0}
                                variant="link"
                                ariaLabel="delete-model-button"
                                onClick={handleDeleteModel}
                              >
                                <SpaceBetween direction="horizontal" size="xs">
                                  <Icon name="remove" variant="error" />
                                  <Box variant="span" color="text-status-error">
                                    Delete
                                  </Box>
                                </SpaceBetween>
                              </Button>
                            </SpaceBetween>
                          </div>
                        </div>
                      }
                    >
                      <Button disabled={selected.length === 0}>Delete Model</Button>
                    </Popover>
                  )}
                  <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    onConfirm={({ detail }) => setPreferences(detail)}
                    preferences={preferences}
                    contentDisplayPreference={contentDisplayPrefernece}
                  />
                </SpaceBetween>
              </div>
            }
          >
            Models to Receive
          </Header>
        }
        filter={
          <>
            <div className="model-filter">
              <ScanInput
                placeholder="Scan MPN"
                errors={errorText}
                inputValue={mpnInput || ''}
                onScan={handleScanModel}
                loading={loading}
                clearOnSubmit={true}
                readOnly={loading}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                // Additional characters that are allowed in the MPN. This will be added to the regex
                // Per Townsend, there are no real restrictions on MPN. As mitigation, I am updating regex.
                // we should consider updating this logic to remove the regex.
                // See ticket https://t.corp.amazon.com/V1377528246/communication.
                // MPN characters obtained from attached spreadsheet
                additionalChars="*()/+=:#%&?'\[\]"
                controlId="scan-mpn-input"
                // If enable MPNCopyPaste is set to true (not prod) or if receive type is INVENTORY_ADJUSTMENT (including prod)
                disableCopy={(!featureFlags?.enableMPNCopyPaste && !isAdjustment) || false}
              />
              <Box margin={{ left: 's' }}>
                <Button
                  ariaLabel="scan-model-button"
                  disabled={mpnInput?.length === 0}
                  onClick={handleScanModelButtonClick}
                >
                  Add MPN
                </Button>
              </Box>
              {showMPNSearch && (
                <MPNAutoSuggestion
                  mpnInput={mpnInput}
                  suggestions={suggestions}
                  onHandleClickOption={onHandleClickOption}
                  activeIndex={activeIndex}
                  hideSuggest={hideSuggest}
                />
              )}
            </div>
            <Box margin={{ top: 's' }}>
              {error && !multipleIPNModels ? (
                <Alert
                  statusIconAriaLabel="Error"
                  type="error"
                  dismissible
                  dismissAriaLabel="dismiss-wrong-mpn"
                  onDismiss={() => {
                    setError(null);
                  }}
                >
                  {error !== MODEL_ERROR_CODES.runtime_client_error ? (
                    <ErrorMessages
                      code={error}
                      selectedModel={selectedModel}
                      referenceId={referenceId}
                      scannedModelId={request?.mpn}
                      isPO={isPO}
                    />
                  ) : (
                    <ClientError />
                  )}
                </Alert>
              ) : (
                ''
              )}
            </Box>
          </>
        }
      />
      {!hasWarnings && multipleManufacturersPresent && (
        <ManufacturerSelectionModal
          mpnToIPNMapping={mpnToIPNMapping}
          onDismiss={(): void => {
            setMPNToIPNMapping(undefined);
            setAsns([]);
          }}
          onConfirm={handleManufacturerSelected}
        />
      )}
      {!hasWarnings && mpnToIPNMapping && !multipleManufacturersPresent && (
        <MPNToIPNMappings
          mpnToIPNMapping={mpnToIPNMapping}
          onDismiss={() => {
            setMPNToIPNMapping(undefined);
            setAsns([]);
          }}
          onConfirm={handleMpnMappingSelected}
        />
      )}
      {hasWarnings && (
        <ModelNotInASNConfirmation
          onDismiss={onHandleDismissWarning}
          onConfirm={onHandleConfirmWarningMPN}
          model={selectedModel}
          displayScosReceiptsLink={isASNPO || isPO}
        >
          <ErrorMessages code={'model_is_not_expected'} />
        </ModelNotInASNConfirmation>
      )}
      {!hasWarnings && showMultipleModels && (
        <MultipleIPNModels
          models={asns || []}
          onDismiss={handleDismissAsnISelection}
          onConfirm={handleModelConfirm}
          mpnMappingSelected={mpnMappingSelected}
          isPo={isPO}
        />
      )}
      {!mpnSearch && asnPos?.length > 0 && featureFlags?.showReleaseOneExperience && (
        <MultipleReferences
          references={totalAsnPos}
          onConfirm={onConfirmMultipleReferences}
          onDismiss={onCancelMultipleReferences}
          withPagination
          loading={asnLoading}
          pageIndx={asnPOIndx}
          pagesCount={canFetchMore ? Math.ceil(asnPos.length / DEFAULT_TABLE_TOTAL_ROWS) + 1 : 1}
          openEnd={canFetchMore}
          onUpdatePage={refetchAsns}
          title={`Multiple ASN's found with the scanned PO and MPN - ${reference?.receiveReference.externalReferenceValue} + ${mpnInput}`}
          visibleColumns={['carrier', 'supplier', 'trackingId', 'sitename', 'shipmentId']}
        />
      )}
    </Box>
  );
};
